
import { useState, useEffect, useMemo } from 'react';
import { useTheme } from 'next-themes';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    ShieldAlert,
    CreditCard,
    Loader2,
    CheckCircle,
    AlertCircle,
    Globe,
    Check,
    ArrowLeft,
    UserPlus
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/utils/appsettings';
import { abandonUnpaidSubscription } from '@/utils/billingUtils';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { WalletPayButton, type WalletBillingDetails } from '@/components/stripe/WalletPayButton';
import { StripeCardElementOptions } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import countryList from 'country-list';
import {
    validatePostalCode,
    getPostalCodePlaceholder,
    requiresPostalCode
} from '@/utils/postalCodeValidation';
import { useAuthToken } from "@/contexts/AuthContext";
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { PromoCodeInput, AppliedPromoDetails } from '@/components/PromoCodeInput';
import { useCheckoutPreview } from '@/hooks/useCheckoutPreview';
import { TaxAndBusinessFields, CustomerType } from '@/components/billing/TaxAndBusinessFields';

// Stripe renders CardElement in a cross-origin iframe, so Tailwind dark: classes can't reach it —
// theme it with literal colors keyed off the live resolved theme, same as CreditPurchaseModal.tsx.
const getCardElementOptions = (isDark: boolean): StripeCardElementOptions => ({
    style: {
        base: {
            fontSize: '16px',
            color: isDark ? '#f5f2ed' : '#1e293b',
            fontFamily:
                'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            '::placeholder': { color: isDark ? '#8b93a8' : '#9ca3af' },
            iconColor: isDark ? '#8b93a8' : '#9ca3af',
        },
        invalid: { color: isDark ? '#f87171' : '#ef4444', iconColor: isDark ? '#f87171' : '#ef4444' },
    },
    hidePostalCode: true,
});

interface Plan {
    id: string;
    name: string;
    display_name: string;
    description?: string;
    price_monthly: number;
    price_yearly?: number;
    features?: Record<string, any>;
    team_collaboration: boolean;
}

interface CreateTeamDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface SavedPaymentMethod {
    id: string;
    card_brand: string;
    card_last_four: string;
    card_exp_month: number;
    card_exp_year: number;
    is_default: boolean;
    nickname?: string;
}

export const CreateTeamDialog = ({ open, onOpenChange }: CreateTeamDialogProps) => {
    const [teamName, setTeamName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { toast } = useToast();
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const token = useAuthToken();
    const { refreshWorkspaces, switchWorkspace } = useWorkspace();
    const { resolvedTheme } = useTheme();
    const cardElementOptions = useMemo(() => getCardElementOptions(resolvedTheme === 'dark'), [resolvedTheme]);

    // Plan state — fetched from DB
    const [teamPlans, setTeamPlans] = useState<Plan[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [loadingPlan, setLoadingPlan] = useState(false);

    // Payment state
    const [paymentMethods, setPaymentMethods] = useState<SavedPaymentMethod[]>([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
    const [useNewCard, setUseNewCard] = useState(false);
    const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
    const [savePaymentMethod, setSavePaymentMethod] = useState(true);

    // Address state
    const [addressLine1, setAddressLine1] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [billingCountry, setBillingCountry] = useState('US');
    const [postalCode, setPostalCode] = useState('');
    const [cardholderName, setCardholderName] = useState('');
    const [detectingCountry, setDetectingCountry] = useState(false);

    const [error, setError] = useState<string | null>(null);

    // Promo / voucher state — mirrors BillingPayment.tsx
    const [appliedVoucherCode, setAppliedVoucherCode] = useState<string | null>(null);
    const [appliedDiscountType, setAppliedDiscountType] = useState<string | null>(null);
    const [appliedDiscountValue, setAppliedDiscountValue] = useState<number | null>(null);
    const [appliedBonusCredits, setAppliedBonusCredits] = useState<number | null>(null);

    // Individual/Business + tax ID state — mirrors BillingPayment.tsx
    const [customerType, setCustomerType] = useState<CustomerType>('individual');
    const [businessName, setBusinessName] = useState('');
    const [taxIdType, setTaxIdType] = useState('');
    const [taxIdValue, setTaxIdValue] = useState('');

    // Two-step new-card checkout: 'details' (billing address) -> 'payment' (card entry).
    // Mirrors BillingPayment.tsx — saved-card checkout has nothing new to collect, so it
    // never leaves 'details'.
    const [checkoutStep, setCheckoutStep] = useState<'details' | 'payment'>('details');

    // Get countries sorted alphabetically
    const countries = useMemo(() => {
        const data = countryList.getData();
        return data.sort((a, b) => a.name.localeCompare(b.name));
    }, []);

    // Derived
    const teamPlan = useMemo(() => teamPlans.find(p => p.id === selectedPlanId), [teamPlans, selectedPlanId]);
    const postalCodeRequired = requiresPostalCode(billingCountry);
    const planPrice = teamPlan?.price_monthly ?? 0;
    const formattedPrice = planPrice > 0 ? `$${(planPrice).toFixed(2)}` : 'Free';
    const planFeatures = teamPlan?.features
        ? Object.entries(teamPlan.features).filter(([, v]) => v)
        : [];

    // Final price after applying any promo code — mirrors BillingPayment.tsx
    const finalPrice = useMemo(() => {
        if (!appliedDiscountType || appliedDiscountValue === null) return planPrice;
        if (appliedDiscountType === 'percentage') return planPrice * (1 - appliedDiscountValue / 100);
        if (appliedDiscountType === 'fixed_amount') return Math.max(planPrice - appliedDiscountValue, 0);
        return planPrice;
    }, [planPrice, appliedDiscountType, appliedDiscountValue]);
    const hasDiscount = appliedVoucherCode !== null && finalPrice < planPrice;
    const formattedFinalPrice = finalPrice > 0 ? `$${finalPrice.toFixed(2)}` : 'Free';

    // Live tax/discount preview — no workspace_id yet, the team doesn't exist until
    // /team/confirm runs, same reasoning as the promo-code resolution below.
    const { preview } = useCheckoutPreview({
        token,
        planId: teamPlan?.id,
        billingCycle: 'monthly',
        voucherCode: appliedVoucherCode,
        customerType,
        businessName,
        taxIdType,
        taxIdValue,
        // Saved-card checkout doesn't collect an address here — send an empty one, plus
        // which saved card is selected, so the backend can use that specific card's own address.
        address: useNewCard
            ? { line1: addressLine1, city, state, postal_code: postalCode, country: billingCountry }
            : {},
        savedPaymentMethodId: !useNewCard ? selectedPaymentMethod : undefined,
        // Both flows only resolve the total once billing details are submitted (step
        // 'payment') — new-card to avoid refetching on every keystroke/country change while
        // still typing, saved-card so the same "confirm before you can pay" gate applies
        // instead of showing the total immediately on mount.
        enabled: planPrice > 0 && checkoutStep === 'payment',
    });
    const displayTotal = preview && !preview.requires_more_info ? preview.total : finalPrice;
    const formattedDisplayTotal = displayTotal > 0 ? `$${displayTotal.toFixed(2)}` : 'Free';
    const selectedSavedMethod = paymentMethods.find((pm) => pm.id === selectedPaymentMethod);
    const walletBillingDetails: WalletBillingDetails = {
        name: cardholderName,
        address: {
            line1: addressLine1,
            city,
            state,
            country: billingCountry,
            postal_code: postalCodeRequired ? postalCode : undefined,
        },
    };
    // True from the moment the payment step is reached until the preview request finishes
    // (success, "can't compute", or failure) — used to show a skeleton instead of flashing
    // the pre-tax total and then jumping to the tax-inclusive one once it resolves.
    const resolvingTotal = checkoutStep === 'payment' && planPrice > 0 && !preview;

    // A promo code is validated against a specific plan, so clear it if the plan changes.
    useEffect(() => {
        setAppliedVoucherCode(null);
        setAppliedDiscountType(null);
        setAppliedDiscountValue(null);
        setAppliedBonusCredits(null);
    }, [selectedPlanId]);

    useEffect(() => {
        if (open && token) {
            fetchTeamPlan();
            fetchPaymentMethods();
            detectUserCountry();
        }
    }, [open, token]);

    const fetchTeamPlan = async () => {
        setLoadingPlan(true);
        try {
            const response = await axiosInstance.get('/billing/plans?team=true');
            const plans: Plan[] = response.data;
            const validTeamPlans = plans.filter(p => p.team_collaboration);
            setTeamPlans(validTeamPlans);

            if (validTeamPlans.length === 1) {
                // Auto-select if there is exactly 1 plan
                setSelectedPlanId(validTeamPlans[0].id);
            }
        } catch (err) {
        } finally {
            setLoadingPlan(false);
        }
    };

    const fetchPaymentMethods = async () => {
        setLoadingPaymentMethods(true);
        try {
            const response = await axiosInstance.get('/billing/payment-methods');
            const methods = response.data.payment_methods || [];
            setPaymentMethods(methods);

            const defaultMethod = methods.find((pm: SavedPaymentMethod) => pm.is_default);
            if (defaultMethod) {
                setSelectedPaymentMethod(defaultMethod.id);
                setUseNewCard(false);
            } else if (methods.length > 0) {
                setSelectedPaymentMethod(methods[0].id);
                setUseNewCard(false);
            } else {
                setUseNewCard(true);
            }
        } catch (err) {
            setUseNewCard(true);
        } finally {
            setLoadingPaymentMethods(false);
        }
    };

    const detectUserCountry = async () => {
        setDetectingCountry(true);
        try {
            const response = await fetch('https://ipapi.co/json/');
            const data = await response.json();
            if (data.country_code && countries.find(c => c.code === data.country_code)) {
                setBillingCountry(data.country_code);
            }
        } catch {
            // Ignore, keep default US
        } finally {
            setDetectingCountry(false);
        }
    };

    const handleCountryChange = (newCountry: string) => {
        setBillingCountry(newCountry);
        setPostalCode('');
    };

    const handleUseNewCardToggle = () => {
        setUseNewCard(!useNewCard);
        setCheckoutStep('details');
    };

    // Single source of truth for "is the billing-details step complete" — used both by the
    // Next button (step 1 -> 2) and defensively inside handleCreate, so the rule only ever
    // lives in one place instead of drifting between two copies of it.
    const validateBillingDetails = (): string | null => {
        if (!addressLine1 || !city || !state || !cardholderName) {
            return 'Please fill in all address fields';
        }
        if (postalCodeRequired && !postalCode) {
            return 'Postal code is required';
        }
        if (postalCodeRequired && !validatePostalCode(postalCode, billingCountry)) {
            return 'Invalid postal code';
        }
        return null;
    };

    const handleNextFromDetails = () => {
        if (useNewCard) {
            const validationError = validateBillingDetails();
            if (validationError) {
                setError(validationError);
                return;
            }
        } else if (!selectedPaymentMethod) {
            setError('Please select a payment method.');
            return;
        }
        setError(null);
        setCheckoutStep('payment');
    };

    // walletPaymentMethodId comes from WalletPayButton (Apple/Google Pay) and replaces the CardElement step only.
    const handleCreate = async (walletPaymentMethodId?: string) => {
        if (!teamName.trim()) {
            setError('Workspace name is required');
            return;
        }
        if (!teamPlan) {
            setError('Unable to load the plan. Please try again.');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            let paymentMethodId = walletPaymentMethodId ?? selectedPaymentMethod;

            if (useNewCard && !walletPaymentMethodId) {
                if (!stripe || !elements) throw new Error('Stripe not initialized');
                // Defensive re-check — the billing-details step already validated this
                // before advancing here, but never trust a prior step's validation is
                // still true at charge time.
                const validationError = validateBillingDetails();
                if (validationError) throw new Error(validationError);

                const cardElement = elements.getElement(CardElement);
                if (!cardElement) throw new Error('Card element not found');

                const billingDetails: any = {
                    name: cardholderName,
                    address: {
                        line1: addressLine1,
                        city,
                        state,
                        country: billingCountry,
                    },
                };
                if (postalCodeRequired) {
                    billingDetails.address.postal_code = postalCode;
                }

                const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardElement,
                    billing_details: billingDetails,
                });

                if (pmError) throw new Error(pmError.message);
                if (!paymentMethod) throw new Error('Failed to create payment method');
                paymentMethodId = paymentMethod.id;
            }

            // Initiate backend subscription
            const initiateResponse = await axiosInstance.post('/billing/team/initiate', {
                plan_id: teamPlan.id,
                workspace_name: teamName,
                payment_method_id: paymentMethodId,
                voucher_code: appliedVoucherCode ?? undefined,
                customer_type: customerType,
                business_name: customerType === 'business' ? businessName : undefined,
                tax_id_type: customerType === 'business' ? taxIdType : undefined,
                tax_id_value: customerType === 'business' ? taxIdValue : undefined,
            });

            const { client_secret, subscription_id } = initiateResponse.data;

            // Confirm payment if client_secret present
            if (client_secret) {
                const { error: confirmError, paymentIntent } = await stripe!.confirmCardPayment(client_secret);
                const paymentFailed = Boolean(confirmError) || paymentIntent?.status !== 'succeeded';
                // A declined attempt would otherwise stay alive in Stripe, retried and billed beside the next one.
                if (paymentFailed && subscription_id) abandonUnpaidSubscription(subscription_id);
                if (confirmError) throw new Error(confirmError.message);
                if (paymentIntent.status !== 'succeeded')
                    throw new Error(`Payment failed: ${paymentIntent.status}`);
            }

            // Confirm workspace creation
            const confirmResponse = await axiosInstance.post('/billing/team/confirm', {
                subscription_id,
                workspace_name: teamName,
            });

            // Optionally save new payment method
            if (useNewCard && savePaymentMethod && paymentMethodId) {
                try {
                    await axiosInstance.post('/billing/payment-methods', {
                        payment_method_id: paymentMethodId,
                        set_as_default: true,
                    });
                } catch (saveErr) {

                }
            }

            toast({
                title: 'Workspace created',
                description: `"${teamName}" is ready. You can invite people into it from Team & workspace.`,
                className: 'bg-green-50 border-green-200 text-green-900',
            });

            // Refresh workspaces and auto-switch to the new one
            const updatedWorkspaces = await refreshWorkspaces();
            if (confirmResponse.data?.workspace_id) {
                switchWorkspace(confirmResponse.data.workspace_id, updatedWorkspaces);
            }

            onOpenChange(false);
        } catch (err: any) {
            setError(err.message || 'An unexpected error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* No z-[...] override here — the shared DialogContent already defaults to
                z-[10020] matching its own DialogOverlay. This used to hardcode z-[100],
                which sat BELOW that overlay (a full-screen 80%-opacity black backdrop
                fixed at z-[10020]), so the overlay rendered on top of this dialog's own
                content: everything read as washed-out/dim regardless of theme, and every
                click landed on the overlay's close-on-click-outside handler instead of
                the form fields underneath. */}
            {/* flex flex-col overrides the base grid-rows-[minmax(0,1fr)]: that track allows
                row 1 (DialogHeader, here) to shrink to 0 when content exceeds max-h, and a
                shrunk grid item still paints at full size, overlapping the row below it.
                100vh shrinks with browser zoom, so this only showed up at high zoom once
                content stopped fitting max-h-[calc(100vh-8rem)] — same fix FigmaBuildDialog
                already uses for its scrollable content. */}
            <DialogContent className="sm:max-w-2xl max-h-[calc(100vh-8rem)] overflow-y-auto flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-foreground">
                        Create workspace
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        {loadingPlan ? (
                            <span className="text-muted-foreground text-sm">Loading plan details...</span>
                        ) : teamPlans.length === 0 ? (
                            <span className="text-red-500 dark:text-red-400 text-sm">No plans available for a new workspace.</span>
                        ) : (
                            <>
                                {teamPlan?.description && (
                                    <span className="block mb-1">{teamPlan.description}</span>
                                )}
                                {teamPlan && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-950/30 text-green-800 dark:text-green-300">
                                        {formattedPrice}/month
                                    </span>
                                )}
                            </>
                        )}
                    </DialogDescription>
                    <p data-testid="create-workspace-invite-note" className="flex gap-2 pt-1 text-[13px] leading-relaxed text-foreground">
                        <UserPlus className="mt-0.5 h-4 w-4 shrink-0 text-plum" aria-hidden="true" />
                        <span>Once it's created you can invite people into this workspace to build with you.</span>
                    </p>
                </DialogHeader>

                {/* Plan features from DB */}
                {!loadingPlan && planFeatures.length > 0 && (
                    <div className="bg-selected border border-plum/20 rounded-lg px-4 py-3">
                        <p className="text-xs font-semibold text-plum uppercase mb-2">What's included</p>
                        <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
                            {planFeatures.map(([key]) => (
                                <li key={key} className="flex items-center gap-1.5 text-sm text-foreground">
                                    <Check className="w-3.5 h-3.5 text-plum shrink-0" />
                                    <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="grid gap-6 py-4">
                    {/* Workspace name */}
                    <div className="space-y-2">
                        <Label htmlFor="team-name" className="text-foreground font-semibold">
                            Workspace name
                        </Label>
                        <Input
                            id="team-name"
                            value={teamName}
                            onChange={(e) => setTeamName(e.target.value)}
                            placeholder="e.g. Acme Corp Engineering"
                            className="bg-surface text-foreground placeholder:text-muted-foreground border-line focus:border-plum/40 focus:ring-plum/30"
                            disabled={isLoading}
                        />
                    </div>

                    {/* Plan Selection */}
                    {!loadingPlan && teamPlans.length > 1 && (
                        <div className="space-y-2">
                            <Label htmlFor="plan-select" className="text-foreground font-semibold">
                                Select Plan
                            </Label>
                            <select
                                id="plan-select"
                                value={selectedPlanId}
                                onChange={(e) => setSelectedPlanId(e.target.value)}
                                className="flex h-10 w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-plum/30 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={isLoading}
                            >
                                <option value="" disabled className="bg-surface text-foreground">Select a plan</option>
                                {teamPlans.map((plan) => (
                                    <option key={plan.id} value={plan.id} className="bg-surface text-foreground">
                                        {plan.display_name} - ${plan.price_monthly}/month
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Step indicator */}
                    <div className="flex items-center gap-2 text-sm font-medium">
                        <span className={checkoutStep === 'details' ? 'text-plum' : 'text-muted-foreground'}>
                            1. Billing details
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <span className={checkoutStep === 'payment' ? 'text-plum' : 'text-muted-foreground'}>
                            {useNewCard ? '2. Card details' : '2. Review & Pay'}
                        </span>
                    </div>

                    {checkoutStep === 'details' && (
                    <>
                    {/* Promo code */}
                    {!loadingPlan && teamPlan && planPrice > 0 && (
                        <div className="space-y-2">
                            <Label className="text-foreground font-semibold">Promo Code</Label>
                            {/* No workspaceId — the team/workspace doesn't exist yet at this point,
                                it's created only after payment confirms (see /team/initiate). */}
                            <PromoCodeInput
                                key={selectedPlanId}
                                planName={teamPlan.name}
                                originalPrice={planPrice}
                                authToken={token ?? ''}
                                onApplied={(details: AppliedPromoDetails) => {
                                    setAppliedVoucherCode(details.code);
                                    setAppliedDiscountType(details.discountType);
                                    setAppliedDiscountValue(details.discountValue);
                                    setAppliedBonusCredits(details.bonusCredits);
                                }}
                                onCleared={() => {
                                    setAppliedVoucherCode(null);
                                    setAppliedDiscountType(null);
                                    setAppliedDiscountValue(null);
                                    setAppliedBonusCredits(null);
                                }}
                            />
                            {hasDiscount && (
                                <div className="flex items-center justify-between text-sm pt-1">
                                    <span className="text-muted-foreground">Total due today</span>
                                    <span className="text-muted-foreground">
                                        <span className="line-through mr-2">${planPrice.toFixed(2)}</span>
                                        <span className="text-green-600 dark:text-green-400 font-semibold">{formattedFinalPrice}</span>
                                    </span>
                                </div>
                            )}
                            {appliedBonusCredits && (
                                <p className="text-xs text-green-600 dark:text-green-400">
                                    {appliedBonusCredits.toLocaleString()} bonus credits added to your account
                                </p>
                            )}
                        </div>
                    )}

                    <div className="border-t border-line" />

                    {/* Payment Method Selection */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <Label className="text-foreground font-semibold">Payment Method</Label>
                            {paymentMethods.length > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleUseNewCardToggle}
                                    className="text-plum hover:opacity-80 hover:bg-soft"
                                >
                                    {useNewCard ? 'Use Saved Card' : 'Use New Card'}
                                </Button>
                            )}
                        </div>

                        {loadingPaymentMethods ? (
                            <div className="flex justify-center p-4">
                                <Loader2 className="w-6 h-6 animate-spin text-plum" />
                            </div>
                        ) : !useNewCard && paymentMethods.length > 0 ? (
                            <div className="grid gap-3">
                                {paymentMethods.map((pm) => (
                                    <div
                                        key={pm.id}
                                        onClick={() => setSelectedPaymentMethod(pm.id)}
                                        className={`
                                            p-4 border-2 rounded-xl cursor-pointer flex items-center justify-between transition-all
                                            ${selectedPaymentMethod === pm.id
                                                ? 'border-plum/50 bg-selected ring-1 ring-plum/40'
                                                : 'border-line bg-surface hover:border-plum/30'}
                                        `}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-soft rounded-md">
                                                <CreditCard className="w-5 h-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-foreground">
                                                    {pm.nickname || `${pm.card_brand} ending in ${pm.card_last_four}`}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {pm.card_brand} •••• {pm.card_last_four} &bull; Exp: {pm.card_exp_month}/{pm.card_exp_year}
                                                </p>
                                            </div>
                                        </div>
                                        {selectedPaymentMethod === pm.id && (
                                            <CheckCircle className="w-5 h-5 text-plum" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* New card form — field order matches BillingPayment.tsx */
                            <div className="space-y-4 bg-soft p-4 rounded-xl border border-line">
                                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    Billing Address
                                </Label>

                                {/* Cardholder Name */}
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                                        Cardholder Name
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="John Doe"
                                        value={cardholderName}
                                        onChange={(e) => setCardholderName(e.target.value)}
                                        disabled={isLoading}
                                        className="w-full px-3 py-2.5 bg-surface border border-line rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-transparent shadow-sm text-sm"
                                    />
                                </div>

                                {/* Address Line 1 */}
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                                        Address Line 1
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="123 Main St"
                                        value={addressLine1}
                                        onChange={(e) => setAddressLine1(e.target.value)}
                                        disabled={isLoading}
                                        className="w-full px-3 py-2.5 bg-surface border border-line rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-transparent shadow-sm text-sm"
                                    />
                                </div>

                                {/* City / State */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">City</label>
                                        <input
                                            type="text"
                                            placeholder="New York"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            disabled={isLoading}
                                            className="w-full px-3 py-2.5 bg-surface border border-line rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-transparent shadow-sm text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">
                                            State / Province
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="NY"
                                            value={state}
                                            onChange={(e) => setState(e.target.value)}
                                            disabled={isLoading}
                                            className="w-full px-3 py-2.5 bg-surface border border-line rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-transparent shadow-sm text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Billing Country with auto-detect indicator */}
                                <div>
                                    <label className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground mb-1">
                                        <Globe className="w-4 h-4" />
                                        Billing Country
                                        {detectingCountry && (
                                            <span className="text-plum text-xs ml-1">(Auto-detecting...)</span>
                                        )}
                                    </label>
                                    <select
                                        value={billingCountry}
                                        onChange={(e) => handleCountryChange(e.target.value)}
                                        disabled={isLoading || detectingCountry}
                                        className="w-full px-3 py-2.5 bg-surface border border-line rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed shadow-sm text-sm"
                                    >
                                        {countries.map((c) => (
                                            <option key={c.code} value={c.code} className="bg-surface text-foreground">
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Postal Code (conditional) */}
                                {postalCodeRequired && (
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">
                                            Postal Code
                                        </label>
                                        <input
                                            type="text"
                                            placeholder={getPostalCodePlaceholder(billingCountry)}
                                            value={postalCode}
                                            onChange={(e) => setPostalCode(e.target.value.toUpperCase())}
                                            disabled={isLoading}
                                            className="w-full px-3 py-2.5 bg-surface border border-line rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-transparent shadow-sm text-sm"
                                        />
                                        {postalCode && !validatePostalCode(postalCode, billingCountry) && (
                                            <p className="text-red-500 dark:text-red-400 text-xs mt-1">
                                                Invalid postal code for{' '}
                                                {countries.find((c) => c.code === billingCountry)?.name}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Individual / Business */}
                    {!loadingPlan && teamPlan && planPrice > 0 && (
                        <TaxAndBusinessFields
                            customerType={customerType}
                            onCustomerTypeChange={setCustomerType}
                            businessName={businessName}
                            onBusinessNameChange={setBusinessName}
                            taxIdType={taxIdType}
                            onTaxIdTypeChange={setTaxIdType}
                            taxIdValue={taxIdValue}
                            onTaxIdValueChange={setTaxIdValue}
                            disabled={isLoading}
                        />
                    )}

                    </>
                    )}

                    {/* Payment step — card entry for new-card, final confirmation for
                        saved-card (nothing left to collect there, just the resolved total). */}
                    {checkoutStep === 'payment' && (
                        <>
                            <button
                                type="button"
                                onClick={() => setCheckoutStep('details')}
                                disabled={isLoading}
                                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 w-fit"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to billing details
                            </button>

                            {!useNewCard && (
                                <div className="flex items-center justify-between bg-soft border border-line rounded-xl p-4">
                                    <div className="flex items-center gap-3">
                                        <CreditCard className="w-5 h-5 text-muted-foreground" />
                                        <span className="text-foreground font-medium">
                                            {selectedSavedMethod
                                                ? (selectedSavedMethod.nickname || `${selectedSavedMethod.card_brand} ending in ${selectedSavedMethod.card_last_four}`)
                                                : 'Saved card'}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setCheckoutStep('details')}
                                        className="text-sm font-medium text-plum hover:opacity-80"
                                    >
                                        Change
                                    </button>
                                </div>
                            )}

                            {useNewCard && (
                                <>
                                    {!resolvingTotal && preview && !preview.requires_more_info && teamPlan && (
                                        <WalletPayButton
                                            mode="subscription"
                                            amountCents={preview.total * 100}
                                            recurring={{
                                                label: `Joylo ${teamPlan.display_name}`,
                                                interval: 'month',
                                                amountCents: preview.total * 100,
                                            }}
                                            billingDetails={walletBillingDetails}
                                            disabled={isLoading || !teamName.trim()}
                                            canOpen={() => !!teamName.trim() && !validateBillingDetails()}
                                            onPaymentMethod={handleCreate}
                                        />
                                    )}
                                    {/* Card Details */}
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">
                                            Card Information
                                        </label>
                                        <div
                                            className={`w-full px-4 py-3 bg-surface border border-line rounded-lg focus-within:ring-2 focus-within:ring-plum/30 focus-within:border-transparent shadow-sm ${isLoading ? 'opacity-50 pointer-events-none' : ''
                                                }`}
                                        >
                                            <CardElement
                                                options={{ ...cardElementOptions, disabled: isLoading }}
                                            />
                                        </div>
                                    </div>

                                    {/* Save card checkbox */}
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="save-card"
                                            checked={savePaymentMethod}
                                            onChange={(e) => setSavePaymentMethod(e.target.checked)}
                                            disabled={isLoading}
                                            className="h-4 w-4 rounded border-line accent-[hsl(var(--plum))] focus:ring-plum/30"
                                        />
                                        <Label
                                            htmlFor="save-card"
                                            className="font-normal text-sm text-muted-foreground cursor-pointer"
                                        >
                                            Save this card for future billing
                                        </Label>
                                    </div>
                                </>
                            )}

                            {/* Order breakdown — directly beside the action that charges
                                this amount, so the tax line can't be missed. */}
                            <div className="border-t border-line pt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{teamPlan?.display_name}, monthly</span>
                                    <span className="text-foreground">${planPrice.toFixed(2)}</span>
                                </div>
                                {hasDiscount && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-green-700 dark:text-green-300">Discount ({appliedVoucherCode})</span>
                                        <span className="text-green-700 dark:text-green-300">-${(planPrice - finalPrice).toFixed(2)}</span>
                                    </div>
                                )}
                                {resolvingTotal ? (
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-muted-foreground">VAT</span>
                                        <span className="inline-block h-4 w-16 bg-soft rounded animate-pulse" />
                                    </div>
                                ) : preview?.requires_more_info ? (
                                    <p className="text-sm text-muted-foreground">
                                        We couldn't calculate VAT for this address — the total below doesn't include tax yet.
                                    </p>
                                ) : preview && (
                                    preview.reverse_charge ? (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-green-700 dark:text-green-300 font-medium">VAT (reverse charge)</span>
                                            <span className="text-green-700 dark:text-green-300 font-medium">$0.00</span>
                                        </div>
                                    ) : preview.tax_amount > 0 ? (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">
                                                VAT{preview.tax_breakdown[0]?.percentage ? ` (${preview.tax_breakdown[0].percentage}%)` : ''}
                                            </span>
                                            <span className="text-foreground">${preview.tax_amount.toFixed(2)}</span>
                                        </div>
                                    ) : null
                                )}
                                {preview?.tax_id_rejected && (
                                    <p className="text-xs text-muted-foreground">
                                        We couldn't validate the tax ID provided — standard rates were applied.
                                    </p>
                                )}
                                <div className="border-t border-line pt-3 flex justify-between items-center">
                                    <div>
                                        <p className="font-bold text-foreground">Total due today</p>
                                        {!resolvingTotal && (
                                            <p className="text-xs text-muted-foreground">
                                                {hasDiscount ? 'Renews at full price monthly' : `Renews ${formattedDisplayTotal} monthly`}
                                            </p>
                                        )}
                                    </div>
                                    {resolvingTotal ? (
                                        <span className="inline-block h-8 w-24 bg-soft rounded-lg animate-pulse" />
                                    ) : (
                                        <span className="text-2xl font-bold text-plum">{formattedDisplayTotal}</span>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 p-3 rounded-md border border-red-100 dark:border-red-900/40">
                            <AlertCircle className="h-4 w-4 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Security notice */}
                    <div className="bg-soft border border-line rounded-xl p-3 flex items-start gap-3">
                        <ShieldAlert className="h-5 w-5 text-plum mt-0.5 shrink-0" />
                        <div className="text-sm text-foreground">
                            <p className="text-sm font-semibold">Secure payment</p>
                            <p className="text-muted-foreground mt-0.5 text-xs">
                                Your payment is processed securely by Stripe. First month charged immediately.
                            </p>
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                        className="border-line text-muted-foreground hover:text-foreground hover:bg-soft"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={checkoutStep === 'details' ? handleNextFromDetails : () => handleCreate()}
                        disabled={
                            isLoading ||
                            !teamName.trim() ||
                            (!useNewCard && !selectedPaymentMethod) ||
                            loadingPlan ||
                            !selectedPlanId ||
                            resolvingTotal
                        }
                        className="bg-gradient-sunset hover:opacity-90 text-white"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Processing...
                            </>
                        ) : checkoutStep === 'details' ? (
                            useNewCard ? 'Next: Card Details' : 'Next: Review & Pay'
                        ) : resolvingTotal ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Calculating total…
                            </>
                        ) : (
                            <>
                                Pay {loadingPlan ? '...' : formattedDisplayTotal} &amp; create workspace
                                <Check className="ml-2 h-4 w-4" />
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
